import { Button } from '@bizora/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { createTenantSignupRequest, fetchCountries, fetchVerticals } from '@bizora/sdk'
import { useQuery, useMutation } from '@tanstack/react-query'
import { CheckCircle2, Loader2, MapPin, Building2, User, Info } from 'lucide-react'

export function Register() {
  const navigate = useNavigate()
  const [step, setStep] = useState<'form' | 'success'>('form')
  const [signupRequestId, setSignupRequestId] = useState<string | null>(null)
  
  const [formData, setFormData] = useState({
    // Business Information
    businessName: '',
    businessDescription: '',
    vertical: '',
    
    // Contact Person Information
    contactPersonName: '',
    contactEmail: '',
    contactPhone: '',
    countryCode: '+91',
    
    // Address Information
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'India'
  })

  // Fetch countries and verticals
  const { data: countries = [], isLoading: isLoadingCountries } = useQuery({
    queryKey: ['countries'],
    queryFn: fetchCountries
  })

  const { data: verticals = [], isLoading: isLoadingVerticals } = useQuery({
    queryKey: ['verticals'],
    queryFn: () => fetchVerticals(true) // Include inactive for signup
  })

  // Signup mutation
  const signupMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      return await createTenantSignupRequest({
        business_name: data.businessName,
        business_description: data.businessDescription || undefined,
        vertical: data.vertical || undefined,
        contact_person_name: data.contactPersonName,
        contact_email: data.contactEmail,
        contact_phone: data.contactPhone,
        country_code: data.countryCode,
        address_line1: data.addressLine1 || undefined,
        address_line2: data.addressLine2 || undefined,
        city: data.city || undefined,
        state: data.state || undefined,
        postal_code: data.postalCode || undefined,
        country: data.country || undefined
      })
    },
    onSuccess: (data) => {
      setSignupRequestId(data.id)
      setStep('success')
    },
    onError: (error: any) => {
      console.error('Signup error:', error)
      alert(error?.message || 'Failed to submit signup request. Please try again.')
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    if (!formData.businessName.trim()) {
      alert('Please enter your business name')
      return
    }
    if (!formData.contactPersonName.trim()) {
      alert('Please enter contact person name')
      return
    }
    if (!formData.contactEmail.trim() || !formData.contactEmail.includes('@')) {
      alert('Please enter a valid email address')
      return
    }
    if (!formData.contactPhone.trim()) {
      alert('Please enter a contact phone number')
      return
    }
    
    signupMutation.mutate(formData)
  }

  // Get country options
  // Group by calling code to handle duplicates (e.g., +1 for USA and Canada)
  const countryOptions = countries.map(country => ({
    value: country.calling_code,
    label: `${country.flag_emoji || ''} ${country.calling_code} ${country.name}`,
    code: country.code, // Use country code for unique key
    id: country.id // Use country id as fallback unique identifier
  }))

  if (step === 'success') {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-6">
            <CheckCircle2 className="w-10 h-10 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-4xl font-bold text-neutral-900 dark:text-white mb-4">
            Signup Request Submitted!
          </h1>
          <p className="text-xl text-neutral-600 dark:text-neutral-400 mb-8">
            Thank you for your interest in Bizora
          </p>
        </div>

        <div className="bg-white dark:bg-brand-900/60 border border-neutral-200 dark:border-white/10 rounded-xl p-8 shadow-lg">
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-neutral-900 dark:text-white mb-1">
                  What's Next?
                </h3>
                <p className="text-sm text-neutral-700 dark:text-neutral-300">
                  Your signup request has been received and is pending review by our platform admin. 
                  Once approved, you'll receive an email with your login credentials and access to your 
                  business portal. This usually takes 1-2 business days.
                </p>
              </div>
            </div>

            <div className="p-4 bg-neutral-50 dark:bg-white/5 rounded-lg">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                <strong>Request ID:</strong> {signupRequestId?.substring(0, 8)}...
              </p>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">
                We'll send updates to: <strong>{formData.contactEmail}</strong>
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="flex-1"
              >
                Back to Home
              </Button>
              <Button
                onClick={() => {
                  setStep('form')
                  setFormData({
                    businessName: '',
                    businessDescription: '',
                    vertical: '',
                    contactPersonName: '',
                    contactEmail: '',
                    contactPhone: '',
                    countryCode: '+91',
                    addressLine1: '',
                    addressLine2: '',
                    city: '',
                    state: '',
                    postalCode: '',
                    country: 'India'
                  })
                }}
                className="flex-1"
              >
                Submit Another Request
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <div className="text-center mb-12">
        <h1 className="text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white mb-4">
          Register Your Business
        </h1>
        <p className="text-xl text-neutral-600 dark:text-neutral-400">
          Join Bizora and start managing your business online
        </p>
      </div>

      <div className="bg-white dark:bg-brand-900/60 border border-neutral-200 dark:border-white/10 rounded-xl p-6 lg:p-8 shadow-lg">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Business Information Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Business Information
              </h2>
            </div>
            <div className="space-y-4 pl-7">
              <div>
                <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                  Business Name <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="e.g., ABC Bakery"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                  Business Type
                </label>
                <select
                  value={formData.vertical}
                  onChange={(e) => setFormData({ ...formData, vertical: e.target.value })}
                  disabled={isLoadingVerticals}
                  className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600 disabled:opacity-50"
                >
                  <option value="">Select business type (optional)</option>
                  {verticals.map(vertical => (
                    <option key={vertical.slug} value={vertical.slug}>
                      {vertical.icon ? `${vertical.icon} ` : ''}{vertical.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                  Business Description
                </label>
                <textarea
                  value={formData.businessDescription}
                  onChange={(e) => setFormData({ ...formData, businessDescription: e.target.value })}
                  className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="Brief description of your business (optional)"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Contact Person Information Section */}
          <div className="pt-6 border-t border-neutral-200 dark:border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Contact Person Information
              </h2>
            </div>
            <div className="space-y-4 pl-7">
              <div>
                <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                  Contact Person Name <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.contactPersonName}
                  onChange={(e) => setFormData({ ...formData, contactPersonName: e.target.value })}
                  className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                  Email Address <span className="text-danger-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="contact@business.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                  Phone Number <span className="text-danger-500">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                    disabled={isLoadingCountries}
                    className="w-32 rounded-lg border border-neutral-200 dark:border-white/10 px-3 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600 disabled:opacity-50 text-sm"
                  >
                    {countryOptions.map((option, index) => (
                      <option key={option.code || option.id || `country-${index}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    required
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value.replace(/\D/g, '') })}
                    className="flex-1 rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                    placeholder="1234567890"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Address Information Section */}
          <div className="pt-6 border-t border-neutral-200 dark:border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Business Address (Optional)
              </h2>
            </div>
            <div className="space-y-4 pl-7">
              <div>
                <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="Street address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                  placeholder="Apartment, suite, etc. (optional)"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                    placeholder="City"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                    State/Province
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                    placeholder="Postal code"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-900 dark:text-white mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full rounded-lg border border-neutral-200 dark:border-white/10 px-4 py-2 bg-white dark:bg-brand-900/60 text-neutral-900 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-600"
                    placeholder="Country"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-neutral-200 dark:border-white/10">
            <Button 
              type="submit" 
              className="w-full" 
              size="lg"
              disabled={signupMutation.isPending}
            >
              {signupMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Signup Request
                </>
              )}
            </Button>

            <p className="text-sm text-neutral-600 dark:text-neutral-400 text-center mt-4">
              By submitting, you agree to our Terms of Service and Privacy Policy. 
              Your request will be reviewed by our team before approval.
            </p>

            <div className="pt-4 border-t border-neutral-200 dark:border-white/10 mt-4">
              <p className="text-sm text-neutral-600 dark:text-neutral-400 text-center">
                Already have an account?{' '}
                <Link to="/login" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
